import { queryClient } from '$/services/client';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import { validateToken } from '$/services/usecases/authentication';
import {
  GenerateSchoolVerificationFileLinkRequestSchema,
  GetInvitationRequestSchema,
  GetSchoolInvitationsRequestsSchema,
  GetStudentInvitationsRequestSchema,
  GetTeacherInvitationsRequestSchema,
} from '$/services/usecases/invitation/mapper/invitation';
import { studentInvitationsQuery } from '$/services/usecases/invitation/queries';

export const getInvitationById = async (id: string, signal: AbortSignal) => {
  return fetcher(`${backendEndpoints.INVITATIONREQUESTS_URL}/${id}`, {
    method: 'GET',
    parser: (data) => GetInvitationRequestSchema.parse(data).payload,
    signal,
  });
};

export const getStudentInvitations = async (signal: AbortSignal) => {
  await validateToken();
  return fetcher(`${backendEndpoints.INVITATIONREQUESTS_URL}/students`, {
    method: 'GET',
    parser: (data) => GetStudentInvitationsRequestSchema.parse(data).payload,
    includeCredentials: true,
    signal,
  });
};

export const getTeacherInvitations = async (signal: AbortSignal) => {
  await validateToken();
  return fetcher(`${backendEndpoints.INVITATIONREQUESTS_URL}/teachers`, {
    method: 'GET',
    parser: (data) => GetTeacherInvitationsRequestSchema.parse(data).payload,
    includeCredentials: true,
    signal,
  });
};

export const getSchoolInvitationRequests = async (signal: AbortSignal) => {
  await validateToken();
  return fetcher(backendEndpoints.SCHOOLINVITATIONREQUESTS_URL, {
    method: 'GET',
    parser: (data) => GetSchoolInvitationsRequestsSchema.parse(data).payload,
    includeCredentials: true,
    signal,
  });
};

export const createSchoolInvitationRequest = async (payload: {
  name: string;
  department: string;
  address: string;
  website: string;
  phone: string;
  file: File;
  requesterEmail: string;
  isExistingAccount: boolean;
}) => {
  const body = new FormData();
  body.append('name', payload.name);
  body.append('department', payload.department);
  body.append('address', payload.address);
  body.append('website', payload.website);
  body.append('phone', payload.phone);
  body.append('file', payload.file);
  body.append('requesterEmail', payload.requesterEmail);
  body.append('isExistingAccount', payload.isExistingAccount.toString());

  return fetcher(backendEndpoints.SCHOOLINVITATIONREQUESTS_URL, {
    method: 'POST',
    body,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const resendInvitation = async (id: string) => {
  await validateToken();
  return fetcher(`${backendEndpoints.INVITATIONREQUESTS_URL}/${id}/resend`, {
    method: 'POST',
    body: {},
    includeCredentials: true,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const updateTeacherInvitation = async (payload: {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}) => {
  const { id, ...body } = payload;
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.INVITATIONREQUESTS_URL}/teachers/${id}`,
    {
      method: 'PUT',
      body,
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({ queryKey: ['invitationRequest'] });
  }

  return response;
};

export const updateStudentInvitation = async (payload: {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  schoolClassId?: string;
  expirationDate?: string;
}) => {
  const { id, ...body } = payload;
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.INVITATIONREQUESTS_URL}/students/${id}`,
    {
      method: 'PUT',
      body,
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: studentInvitationsQuery.queryKey,
    });
  }

  return response;
};

export const deleteInvitation = async (id: string) => {
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.INVITATIONREQUESTS_URL}/${id}`,
    {
      method: 'DELETE',
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({ queryKey: ['invitationRequest'] });
  }

  return response;
};

export const acceptSchoolInvitationRequest = async (payload: {
  id: string;
  overwriteSchoolId?: string;
}) => {
  await validateToken();
  return fetcher(
    `${backendEndpoints.SCHOOLINVITATIONREQUESTS_URL}/${payload.id}/accept`,
    {
      method: 'POST',
      includeCredentials: true,
      body: { overwriteSchoolId: payload.overwriteSchoolId },
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );
};

export const declineSchoolInvitationRequest = async (id: string) => {
  await validateToken();
  return fetcher(
    `${backendEndpoints.SCHOOLINVITATIONREQUESTS_URL}/${id}/decline`,
    {
      method: 'POST',
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
      body: {},
      includeCredentials: true,
    },
  );
};

export const generateSchoolVerificationFileLink = async (id: string) => {
  await validateToken();
  return fetcher(
    `${backendEndpoints.SCHOOLINVITATIONREQUESTS_URL}/${id}/generateVerificationFileLink`,
    {
      method: 'GET',
      parser: (data) =>
        GenerateSchoolVerificationFileLinkRequestSchema.parse(data),
      includeCredentials: true,
    },
  );
};
